/**
 * CallCentreGate: capability gate for Call Centre pages.
 *
 * Route-level ProtectedRoute only knows roles, but Call Centre capabilities are "role OR right"
 * (a supervisor holds CallCentreSupervisor; a staff member may hold CallCentre.Read without a role).
 * This gate asks useCallCentrePermissions instead.
 */

import type { ReactNode } from 'react';
import { IconLock } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { EmptyState } from '@/components/ui/data-display';
import { useCallCentrePermissions, type CallCentrePermissions } from '@/hooks/callcentre/useCallCentrePermissions';

interface CallCentreGateProps {
  allow: (p: CallCentrePermissions) => boolean;
  children: ReactNode;
}

export function CallCentreGate({ allow, children }: CallCentreGateProps) {
  const perms = useCallCentrePermissions();
  if (!allow(perms)) {
    return (
      <LayoutContainer>
        <EmptyState
          icon={<IconLock size={40} />}
          title="Access denied"
          description="You don't have permission to open this page. Ask a call centre administrator for access."
        />
      </LayoutContainer>
    );
  }
  return <>{children}</>;
}
